// ============================================================================
// @-mention file picker
// ============================================================================
// Triggered by typing "@" in the composer. Searches files under activeCwd
// via GET /api/search-files and lets the user pick one to insert as an
// @path/to/file reference. Mirrors the slash-command picker UX.
import { fmOverlay, fmList, fmQuery } from "./dom.js";
import { escapeHtml } from "./markdown.js";

// Dependencies — set by initFileMentionPicker
let activeCwdRef = () => "";
let inputRef = () => null;
let insertTextRef = null;
let hapticRef = null;

let fmSelectedIndex = -1;
let fmFilteredItems = [];        // SearchHit[] from the server
let fmActiveQuery = "";          // query for which fmFilteredItems is current
let fmPendingQuery = null;       // query waiting to be fetched (or null)
let fmRequestInFlight = false;
let fmRequestToken = 0;          // guards against stale responses
// Range of the @token within input.value: [start, endAfterAt]
let fmTokenStart = -1;

/**
 * Inspect the textarea value at the cursor and decide whether to open,
 * update, or close the file-mention picker. The picker opens when the
 * cursor is immediately after an "@" that is either at the start of the
 * input or preceded by whitespace (so emails like "a@b.com" don't trigger).
 */
export function updateFileMention() {
  const activeCwd = activeCwdRef();
  if (!activeCwd) { closeFileMention(); return; }
  const input = inputRef();
  const val = input.value;
  const pos = input.selectionStart ?? val.length;
  // Walk back from the cursor to find a "@" that begins this token.
  let i = pos;
  while (i > 0) {
    const ch = val[i - 1];
    if (ch === "@") {
      // The "@" must be at start-of-input or preceded by whitespace.
      if (i - 1 === 0 || /\s/.test(val[i - 2])) {
        fmTokenStart = i - 1;
        const query = val.slice(i, pos);
        // If the query contains whitespace or another "@", close — the
        // token is no longer a clean @mention.
        if (/\s|@/.test(query)) { closeFileMention(); return; }
        openFileMention(query);
        return;
      }
      closeFileMention();
      return;
    }
    if (/\s/.test(ch)) break;
    i--;
  }
  closeFileMention();
}

async function openFileMention(query) {
  fmQuery.textContent = query || "search files…";
  // Position the picker above the composer, mirroring the command picker.
  const input = inputRef();
  const rect = input.getBoundingClientRect();
  const pickerWidth = Math.min(440, rect.width);
  fmOverlay.style.left = rect.left + "px";
  fmOverlay.style.bottom = (window.innerHeight - rect.top + 4) + "px";
  fmOverlay.style.right = "auto";
  if (rect.left + pickerWidth > window.innerWidth - 8) {
    fmOverlay.style.left = (window.innerWidth - pickerWidth - 8) + "px";
  }
  fmOverlay.classList.remove("hidden");

  // Show an immediate "loading" state if we have no results yet.
  if (fmFilteredItems.length === 0) {
    fmList.innerHTML = "";
    const loading = document.createElement("div");
    loading.className = "cmd-empty";
    loading.textContent = "Searching files…";
    fmList.appendChild(loading);
  }

  // If a request is already in flight, stash this query as pending so the
  // request loop will pick it up when the current one returns.
  if (fmRequestInFlight) { fmPendingQuery = query; return; }
  await fetchFileMentions(query);
}

async function fetchFileMentions(query) {
  // Already current — just re-render.
  if (query === fmActiveQuery && fmFilteredItems.length > 0) {
    fmSelectedIndex = fmFilteredItems.length > 0 ? 0 : -1;
    renderFileMention();
    return;
  }
  fmActiveQuery = query;
  fmRequestInFlight = true;
  const token = ++fmRequestToken;
  try {
    const url = "/api/search-files?root=" + encodeURIComponent(activeCwdRef()) +
                "&q=" + encodeURIComponent(query);
    const res = await fetch(url, {
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (token !== fmRequestToken) return; // a newer request supersedes us
    if (!res.ok) {
      fmFilteredItems = [];
      fmSelectedIndex = -1;
      renderFileMention();
      return;
    }
    const data = await res.json();
    if (token !== fmRequestToken) return; // stale
    fmFilteredItems = Array.isArray(data.hits) ? data.hits : [];
    fmSelectedIndex = fmFilteredItems.length > 0 ? 0 : -1;
    renderFileMention();
  } catch {
    if (token !== fmRequestToken) return;
    fmFilteredItems = [];
    fmSelectedIndex = -1;
    renderFileMention();
  } finally {
    if (token !== fmRequestToken) return;
    fmRequestInFlight = false;
    // If the user typed more while we were busy, fire a follow-up request.
    if (fmPendingQuery !== null && fmPendingQuery !== fmActiveQuery) {
      const next = fmPendingQuery;
      fmPendingQuery = null;
      fetchFileMentions(next); // fire-and-forget; loop continues
    } else {
      fmPendingQuery = null;
    }
  }
}

export function closeFileMention() {
  fmOverlay.classList.add("hidden");
  fmSelectedIndex = -1;
  fmFilteredItems = [];
  fmActiveQuery = "";
  fmPendingQuery = null;
  fmTokenStart = -1;
  fmRequestInFlight = false;
  fmRequestToken++; // invalidate any in-flight request
  fmQuery.textContent = "";
}
// Prevent mousedown on the overlay from blurring the textarea — item click
// handlers still fire and call input.focus() themselves.
fmOverlay.addEventListener("mousedown", (e) => { e.preventDefault(); });

function renderFileMention() {
  fmList.innerHTML = "";
  fmQuery.textContent = fmActiveQuery || "search files…";
  if (fmFilteredItems.length === 0) {
    const empty = document.createElement("div");
    empty.className = "cmd-empty";
    empty.textContent = fmActiveQuery ? "No matching files" : "No files found";
    fmList.appendChild(empty);
    return;
  }
  for (let i = 0; i < fmFilteredItems.length; i++) {
    const hit = fmFilteredItems[i];
    const item = document.createElement("div");
    item.className = "cmd-item fm-item" + (i === fmSelectedIndex ? " selected" : "");
    const name = document.createElement("div");
    name.className = "cmd-name fm-name";
    const icon = hit.isDir ? "📁 " : "📄 ";
    name.innerHTML = `<span class="fm-icon">${icon}</span><span class="fm-rel">${escapeHtml(hit.rel)}</span>`;
    item.appendChild(name);
    item.addEventListener("click", () => {
      fmSelectedIndex = i;
      executeSelectedFileMention();
    });
    item.addEventListener("mouseenter", () => {
      fmSelectedIndex = i;
      fmList.querySelectorAll(".fm-item").forEach((el, idx) => {
        el.classList.toggle("selected", idx === i);
      });
    });
    fmList.appendChild(item);
  }
}

export function navigateFileMention(dir) {
  if (fmFilteredItems.length === 0) return;
  fmSelectedIndex = (fmSelectedIndex + dir + fmFilteredItems.length) % fmFilteredItems.length;
  const items = fmList.querySelectorAll(".fm-item");
  items.forEach((el, idx) => el.classList.toggle("selected", idx === fmSelectedIndex));
  const sel = items[fmSelectedIndex];
  if (sel) sel.scrollIntoView({ block: "nearest" });
}

export function executeSelectedFileMention() {
  if (fmFilteredItems.length === 0 || fmSelectedIndex < 0) {
    // No selection — just close the picker and let the user keep typing.
    closeFileMention();
    return;
  }
  const hit = fmFilteredItems[fmSelectedIndex];
  insertFileMention(hit);
}

/**
 * Replace the @token in the textarea with `@<rel>` (relative to activeCwd)
 * and keep the cursor positioned just after the inserted reference. If the
 * path contains spaces, wrap it in quotes.
 */
function insertFileMention(hit) {
  const rel = hit.rel || hit.name;
  const needsQuotes = /\s/.test(rel);
  const insertText = "@" + (needsQuotes ? `"${rel}"` : rel);
  const input = inputRef();
  const val = input.value;
  const pos = input.selectionStart ?? val.length;
  // Find the end of the @token (the cursor position when the picker was open).
  // We replace from fmTokenStart up to the current cursor.
  const before = val.slice(0, fmTokenStart);
  const after = val.slice(pos);
  input.value = before + insertText + after;
  const newCursor = before.length + insertText.length;
  input.focus();
  input.setSelectionRange(newCursor, newCursor);
  // Resize the textarea to fit the new content.
  input.style.height = "auto";
  const max = window.innerWidth <= 600 ? 160 : 200;
  input.style.height = Math.min(max, input.scrollHeight) + "px";
  closeFileMention();
}

export function isFileMentionOpen() {
  return !fmOverlay.classList.contains("hidden");
}

/**
 * Initialize the file mention picker.
 * @param {object} deps
 * @param {() => string} deps.activeCwd
 * @param {() => HTMLElement} deps.input
 * @param {(text: string) => void} deps.insertText
 * @param {(type: string) => void} deps.haptic
 */
export function initFileMentionPicker(deps) {
  activeCwdRef = deps.activeCwd;
  inputRef = deps.input;
  insertTextRef = deps.insertText;
  hapticRef = deps.haptic;
}
